import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * Route guard for the whole admin panel.
 *
 * Same hydration/refresh shape as authGuard, plus a role check: this app is only usable by a
 * platform SuperAdmin. A tenant Admin holds a perfectly valid token but no cross-organization
 * authority, so every /api/admin call would come back 403 — better to stop at the door.
 *
 * The check is a convenience for the UI, not a security boundary; authorization is enforced by
 * the API's [Authorize(Roles = "SuperAdmin")] attributes.
 */
export const superAdminGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.initialized$.pipe(
    filter((initialized) => initialized),
    take(1),
    switchMap(() => auth.ensureValidToken()),
    map((valid) => {
      if (!valid) {
        return router.createUrlTree(['/login']);
      }

      return (
        auth.isSuperAdmin() ||
        router.createUrlTree(['/login'], {
          queryParams: { erro: 'sem-permissao' },
        })
      );
    }),
  );
};
